import ProtectedLegalPage from "@/components/ProtectedLegalPage";

export default function Privacy() {
  return (
    <ProtectedLegalPage title="Privacy Policy" lastUpdated="February 2026">
      <p><strong>Company Name:</strong> Techno Sort</p>
      <p><strong>Effective Date:</strong> Last Updated date at the top of this document. © 2026.</p>
      <p><strong>Contact:</strong> technosort.rd@gmail.com (email only).</p>

      <h2>1. Introduction</h2>
      <p>
        Techno Sort ("we", "us", "our", or "the Software") is an offline desktop document classification application primarily intended for businesses and document management professionals in the United States, the European Union, the United Kingdom, and other commercial markets where document management systems are widely used. The Software runs entirely on the end user's local computer.
      </p>
      <p>
        This Privacy Policy explains what limited information we collect to operate licensing and what information we do not collect. By installing, accessing, or using Techno Sort, you confirm that you have read, understood, and agreed to the practices described in this Privacy Policy. If you do not agree, you must not install or use Techno Sort, and you are encouraged to evaluate alternative document classification solutions offered by other vendors.
      </p>

      <h2>2. Most Important: We Cannot See Your Documents</h2>
      <p>
        Techno Sort is a 100% offline desktop application. The Software processes your documents entirely on your own computer. We do not receive, transmit, upload, copy, store, read, scan, index, log, or have any technical means of access to the content of the documents you classify with Techno Sort. The documents you sort remain exclusively on your hardware, under your sole control, at all times.
      </p>

      <h2>3. Information We Do NOT Collect</h2>
      <p>For the avoidance of doubt, we expressly state that we do not collect, process, store, transmit, or have any access to:</p>
      <ul>
        <li>The content of any document you classify, sort, organize, or move using the Software.</li>
        <li>File names, file paths, folder structures, or directory listings on your computer.</li>
        <li>Any text, images, tables, metadata, or attachments contained in your documents.</li>
        <li>Usage analytics about how you interact with the Software's interface.</li>
        <li>Telemetry about which features you use, how often, or for how long.</li>
        <li>Crash reports containing user data, document fragments, or identifiable file information.</li>
        <li>Browsing history, system activity, or any data outside the Software's own runtime.</li>
        <li>Your geographic location, IP address (other than transient handling by our payment processor), or device identifiers beyond the activation fingerprint described below.</li>
      </ul>

      <h2>4. Limited Information We Collect for Licensing</h2>
      <h3>4.1 Purchase Information</h3>
      <ul>
        <li>Your email address (used only to deliver your license key and respond to support emails).</li>
        <li>Your billing name and country (required by our payment processor for tax compliance).</li>
        <li>Payment details (handled exclusively by our third-party payment processor; we never see or store your card number).</li>
      </ul>

      <h3>4.2 License Activation Data</h3>
      <p>On first activation, Techno Sort transmits the following one time, solely to confirm license validity:</p>
      <ul>
        <li>The license key you entered.</li>
        <li>A machine fingerprint — an anonymized one-way hash derived from your CPU, motherboard, network adapter, and disk identifiers. This hash cannot be reversed to reveal your hardware details.</li>
      </ul>
      <p>
        After activation, Techno Sort works fully offline. No document data, file names, folder names, or usage information is ever transmitted. The Software may transmit a small periodic license heartbeat (at most once every 30 days) confirming only that the license is still valid.
      </p>

      <h3>4.3 Support Communications</h3>
      <p>
        If you email us at technosort.rd@gmail.com, we retain your email and the contents of your message solely to respond. We do not use these communications for marketing without your explicit consent.
      </p>

      <h2>5. How We Use the Limited Information</h2>
      <ul>
        <li>To deliver your purchased license key and receipts.</li>
        <li>To verify license validity and machine binding.</li>
        <li>To enforce per-fingerprint refill rules.</li>
        <li>To respond to your support emails on a best-effort basis.</li>
        <li>To send transactional communications such as renewal reminders for Pro 1-Year licenses.</li>
        <li>To comply with applicable legal obligations.</li>
      </ul>

      <h2>6. We Do NOT Sell or Share</h2>
      <p>We do not sell, rent, lease, or trade your personal information to third parties for marketing. We share data only as strictly necessary to operate the Software:</p>
      <ul>
        <li>Our payment processor, who handles your transaction and is bound by their own privacy and PCI compliance obligations.</li>
        <li>Our hosting provider, who stores the license database (license key, fingerprint hash, email, expiry) on secure servers.</li>
        <li>Lawful authorities, where compelled by valid legal process such as a court order in a competent jurisdiction.</li>
      </ul>

      <h2>7. Data Retention</h2>
      <p>
        License records are retained for as long as the license is valid plus a reasonable period thereafter for tax, audit, and dispute-resolution purposes. Support correspondence is retained for up to twenty-four (24) months. You may request deletion of your personal data subject to our legitimate business obligations and applicable law.
      </p>

      <h2>8. Data Security</h2>
      <p>
        We apply commercially reasonable technical and organizational measures to protect the limited data we hold, including encryption of sensitive fields at rest and in transit. However, no method of transmission over the internet or method of electronic storage is one hundred percent (100%) secure. We make no representation or warranty that our security measures will be sufficient to prevent every conceivable form of attack. You acknowledge and accept this inherent risk by using Techno Sort.
      </p>

      <h2>9. Children's Privacy</h2>
      <p>
        Techno Sort is a business productivity tool for adult professionals and is not directed at, intended for, or marketed to children under the age of eighteen (18). We do not knowingly collect personal information from minors. If we become aware that we have inadvertently received personal information from a minor, we will delete it from our records.
      </p>

      <h2>10. International Users and Cross-Border Processing</h2>
      <p>
        The Software is available to users in the United States, the European Union, the United Kingdom, Canada, Australia, and other jurisdictions where its use is lawful. The limited licensing data we hold may be processed by our payment processor and hosting provider in jurisdictions other than yours. By using the Software, you consent to such cross-border processing. If you do not consent, you must not use the Software.
      </p>

      <h2>11. Your Privacy Rights</h2>
      <p>
        Subject to applicable law in your jurisdiction (including but not limited to the European Union General Data Protection Regulation (GDPR), the United Kingdom Data Protection Act, the California Consumer Privacy Act (CCPA), and similar regimes), you may have the right to access, correct, update, port, or request deletion of the personal data we hold about you. To exercise these rights, email technosort.rd@gmail.com with your license key and a description of your request. We will respond within the timeframes required by applicable law on a best-effort basis.
      </p>

      <h2>12. Support Channel</h2>
      <p>
        All communications regarding privacy or any other matter must be sent by email to technosort.rd@gmail.com. We do not provide telephone support, postal correspondence, live chat, in-person meetings, or any other communication channel. Inquiries directed to any channel other than email will not be received or processed.
      </p>

      <h2>13. Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy at any time at our sole discretion. The "Last Updated" date at the top of this document indicates when the most recent changes took effect. Continued use of the Software after any change constitutes your acceptance of the revised Privacy Policy.
      </p>

      <h2>14. Disclaimer</h2>
      <p>
        This Privacy Policy describes our practices in good faith. To the maximum extent permitted by applicable law, Techno Sort and its affiliates, employees, contractors, and licensors shall have no liability whatsoever for any unauthorized access, accidental loss, third-party breach, or any other adverse event affecting any data referenced in this document, regardless of cause.
      </p>

      <h2>15. Contact</h2>
      <p>All privacy inquiries: <strong>technosort.rd@gmail.com</strong> (email only).</p>
      <p>© 2026 Techno Sort. All rights reserved.</p>
    </ProtectedLegalPage>
  );
}
